import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/*
SPDX-FileCopyrightText: 2025 Genome Research Ltd.

SPDX-License-Identifier: MIT
*/
import { Checkbox } from "rsuite";
import { useEffect, useState } from "react";
import { TsDataSource, MultipleSelect, InfoTooltip, Icon, PopUpMessage, SourceTag, EntityMetaToolTip } from "../index";
import { getFlattenedMetaData, getAttributeDetail, getAttributeSources, filterBySource, normaliseCaps, } from "./utils";
function AttributeSelector(props) {
    const { additionalPopulatedFieldData, allowedTypes, attribute, baseUrl, disabledValues, displaySource, endpoint, numPopulatedFields, maxSelections, placeholder, populatedFieldType = "value", recommendedFilterAvailable, renderSearchBySource, setAttribute, onClean, sticky, tooltipContent, customAttributeSelection, } = props;
    const [loading, setLoading] = useState(true);
    const [entityMeta, setEntityMeta] = useState({});
    const [recommendedOn, setRecommendedOn] = useState(false);
    const [sources, setSources] = useState([]);
    const [selectedSources, setSelectedSources] = useState([]);
    const ds = new TsDataSource({ baseUrl });
    useEffect(() => {
        ds.getEntityMeta()
            .then((em) => {
            setEntityMeta(em);
            setSources(getAttributeSources(em, endpoint, customAttributeSelection));
        })
            .finally(() => {
            setLoading(false);
        });
    }, []);
    const searchBy = (keyword, label) => {
        const name = getAttributeDetail(entityMeta, endpoint, label, 'display_name').toLowerCase();
        const description = getAttributeDetail(entityMeta, endpoint, label, 'description').toLowerCase();
        const kw = keyword.toLowerCase();
        return name.includes(kw) || label.includes(kw) || description.includes(kw);
    };
    const menuItem = (displayName, source, key, authoritative) => {
        const disabled = disabledValues && Object.keys(disabledValues).includes(key);
        const tooltipContents = tooltipContent || "disabled";
        return (_jsxs("div", { className: "tol-attribute-selector-menu-item-container", children: [_jsx("div", { className: "tol-attribute-selector-menu-item-inner-container", children: _jsxs("div", { className: "tol-attribute-selector-display-name", children: [displayName, " ", disabled ? (_jsx("span", { className: "tol-attribute-selector-tooltip", children: tooltipContent && (_jsx(InfoTooltip, { disableMarkdown: true, contents: tooltipContents })) })) : (_jsx("span", { className: "tol-attribute-selector-tooltip", children: _jsx(EntityMetaToolTip, { baseUrl: baseUrl, field: key, endpoint: endpoint }) })), _jsxs("div", { className: "tol-attribute-selector-display-key", children: [authoritative === true && _jsx(Icon, { icon: "star" }), _jsx("p", { children: key })] })] }) }), displaySource && source && _jsx(SourceTag, { source: source })] }, key));
    };
    const renderMenuItem = (l, index) => {
        const label = l.props?.children || l;
        const metaData = getFlattenedMetaData(entityMeta, endpoint, label);
        return (_jsx("div", { children: menuItem(metaData["display_name"] ?? normaliseCaps(label), metaData["source"], label, metaData["authoritative"]) }, `${label}-${index}`));
    };
    const renderTotalSelectedItems = (values) => {
        return `
        ${values.length} ${values.length === 1
            ? `${populatedFieldType}`
            : `${populatedFieldType}s`} selected${additionalPopulatedFieldData ||
            `; ${numPopulatedFields} ${numPopulatedFields === 1 ? "filter" : "filters"} populated.`}`;
    };
    const searchBySource = () => {
        const hasActiveSource = selectedSources.length > 0;
        return (_jsxs("div", { className: "tol-attribute-selector-search-by-source-container", children: [_jsx("p", { children: "Filter by source:" }), _jsx("div", { className: "tol-attribute-selector-sources", children: sources.map((source, index) => (_jsx("div", { className: "tol-attribute-selector-sources-inner-container", onClick: () => filterBySource(source, selectedSources, setSelectedSources), children: _jsx(SourceTag, { source: source, className: `${selectedSources.includes(source) ? "active" : ""} ${hasActiveSource && !selectedSources.includes(source)
                                ? "faded"
                                : ""}` }) }, index))) })] }));
    };
    const handleSetAttribute = (newAttribute) => {
        if (maxSelections) {
            if (newAttribute.length > maxSelections) {
                PopUpMessage({
                    type: "warning",
                    message: `You can select a maximum of ${maxSelections} items.`,
                });
                return;
            }
            else {
                setAttribute(newAttribute);
            }
        }
        else {
            setAttribute(newAttribute);
        }
    };
    if (loading)
        return _jsx(_Fragment, {});
    return (_jsxs("div", { className: "tol-attribute-selector", children: [_jsx(MultipleSelect, { className: "tol-attribute-selector-select", block: true, noSelectAll: true, data: Object.keys(getFlattenedMetaData(entityMeta, endpoint))
                    .filter((key) => {
                    const meta = getFlattenedMetaData(entityMeta, endpoint)[key];
                    const typeMatch = !allowedTypes || allowedTypes.includes(meta.python_type);
                    const sourceMatch = selectedSources.length === 0 ||
                        (selectedSources.includes("undefined")
                            ? !sources.includes(meta.source) ||
                                selectedSources.includes(meta.source)
                            : selectedSources.includes(meta.source));
                    const recommendedMatch = meta.authoritative === true;
                    return ((recommendedOn ? recommendedMatch : true) &&
                        typeMatch &&
                        sourceMatch &&
                        (!customAttributeSelection ||
                            customAttributeSelection.includes(key)));
                })
                    .sort((a, b) => {
                    const metaA = getFlattenedMetaData(entityMeta, endpoint)[a];
                    const metaB = getFlattenedMetaData(entityMeta, endpoint)[b];
                    if (metaA.source === null || metaA.source === undefined)
                        return 1;
                    if (metaB.source === null || metaB.source === undefined)
                        return -1;
                    if (metaA.source < metaB.source)
                        return -1;
                    if (metaA.source > metaB.source)
                        return 1;
                    return a.localeCompare(b);
                }), placeholder: placeholder, value: attribute, setValue: handleSetAttribute, renderMenuItem: (l, index) => renderMenuItem(l, index), renderValue: renderTotalSelectedItems, disabledItemValues: disabledValues && [...Object.keys(disabledValues)], searchBy: searchBy, sticky: sticky, renderExtraFooter: renderSearchBySource && searchBySource(), onClean: onClean, onClose: () => setSelectedSources([]) }), recommendedFilterAvailable && (_jsxs("div", { className: "tol-attribute-selector-suggested-toggle", children: [_jsx(Checkbox, { onChange: () => {
                            setRecommendedOn(!recommendedOn);
                        }, checked: recommendedOn }, "recommended-tick-filter"), _jsx("span", { style: { paddingRight: 6 }, onClick: (e) => e.stopPropagation(), children: "Recommended columns." }), _jsx(InfoTooltip, { contents: "Recommended properties are indicated by a star icon." })] }))] }));
}
export default AttributeSelector;
